import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useProject } from "@/context/ProjectContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import Pagination from "@/components/Pagination";
import { MagnifyingGlass, Plus, Camera, ArrowSquareOut, Stack } from "@phosphor-icons/react";

export default function Outward() {
  const { activeProjectId } = useProject();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [loading, setLoading] = useState(true);
  const [type, setType] = useState("all");
  const [category, setCategory] = useState("all");
  const [search, setSearch] = useState("");
  const [outwardCats, setOutwardCats] = useState({ bulk: [], flat: [] });

  const load = async (targetPage = page) => {
    setLoading(true);
    try {
      const params = { page: targetPage, page_size: pageSize };
      if (type !== "all") params.type = type;
      if (category !== "all") params.category = category;
      if (search) params.search = search;
      const { data } = await api.get("/outward", { params });
      setRows(data.items || []);
      setTotal(data.total || 0);
      setTotalPages(data.total_pages || 1);
      setPage(data.page || 1);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/catalog/outward-categories");
        setOutwardCats({ bulk: data.bulk, flat: data.flat });
      } catch {}
    })();
  }, []);

  useEffect(() => {
    load(1);
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, category, activeProjectId]);

  const availableCategories = type === "bulk" ? outwardCats.bulk : type === "flat" ? outwardCats.flat : [...outwardCats.bulk, ...outwardCats.flat];

  return (
    <div className="p-8 sm:p-10 max-w-[1600px] mx-auto" data-testid="outward-list-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-neutral-500 mb-2">Outward</div>
          <h1 className="font-display text-4xl font-bold tracking-tight">Outward Material</h1>
          <p className="text-sm text-neutral-500 mt-2">{total} entr{total === 1 ? "y" : "ies"} · Bulk site outflows + flat-wise consumption</p>
        </div>
        <Link to="/outward/new">
          <Button className="rounded-sm h-11 px-5 bg-primary hover:bg-[#0039CC] text-white font-medium gap-2" data-testid="new-outward-btn">
            <Plus size={16} weight="bold" /> Issue Material
          </Button>
        </Link>
      </div>

      {/* Filters */}
      <div className="bg-white border border-neutral-200 rounded-sm p-4 mb-4 flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[220px]">
          <label className="text-[11px] uppercase tracking-wider text-neutral-500 mb-1 block">Search</label>
          <div className="relative">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load(1)}
              placeholder="Search category, flat, notes..."
              className="rounded-sm pl-9 h-10"
              data-testid="outward-search-input"
            />
          </div>
        </div>
        <div className="w-[180px]">
          <label className="text-[11px] uppercase tracking-wider text-neutral-500 mb-1 block">Type</label>
          <Select value={type} onValueChange={(v) => { setType(v); setCategory("all"); }}>
            <SelectTrigger className="rounded-sm h-10" data-testid="type-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="bulk">Bulk (Site)</SelectItem>
              <SelectItem value="flat">Flat-wise</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="w-[220px]">
          <label className="text-[11px] uppercase tracking-wider text-neutral-500 mb-1 block">Category</label>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="rounded-sm h-10" data-testid="outward-category-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {availableCategories.map((c) => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="bg-white border border-neutral-200 rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 text-neutral-600">
              <tr>
                <th className="text-left px-6 py-3 font-medium text-xs uppercase tracking-wider">Date</th>
                <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wider">Type</th>
                <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wider">Category</th>
                <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wider">Flat / Site</th>
                <th className="text-right px-4 py-3 font-medium text-xs uppercase tracking-wider">Quantity</th>
                <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wider">Photos</th>
                <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wider">Issued To</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="text-center py-12 text-neutral-400">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={8} className="text-center py-16 text-neutral-400">No outward entries yet. Click "Issue Material" to record one.</td></tr>
              ) : (
                rows.map((r, idx) => (
                  <tr key={r.id} className={`border-t border-neutral-100 hover:bg-neutral-50 ${idx % 2 ? "bg-neutral-50/40" : ""}`} data-testid={`outward-row-${r.id}`}>
                    <td className="px-6 py-3 text-neutral-500 tabular-nums whitespace-nowrap">
                      {new Date(r.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 text-[11px] uppercase tracking-wider px-2 py-0.5 border ${r.type === "bulk" ? "bg-[#E8F0FE] text-[#1967D2] border-[#AECBFA]" : "bg-[#F3E8FD] text-[#6B21A8] border-[#D8B4FE]"}`}>
                        {r.type === "bulk" ? <Stack size={11} /> : <ArrowSquareOut size={11} />}
                        {r.type}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-neutral-900">{r.category}</div>
                      {r.subcategory && <div className="text-xs text-neutral-500">{r.subcategory}</div>}
                    </td>
                    <td className="px-4 py-3 text-neutral-700">
                      {r.flat_number ? <span className="font-mono-num">{r.flat_number}</span> : <span className="text-neutral-400">{r.site_location || "—"}</span>}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums font-medium">
                      {r.quantity} <span className="text-neutral-500 text-xs">{r.unit}</span>
                    </td>
                    <td className="px-4 py-3">
                      {r.photos?.length > 0 ? (
                        <span className="inline-flex items-center gap-1 text-xs text-neutral-600">
                          <Camera size={14} /> {r.photos.length}
                        </span>
                      ) : (
                        <span className="text-neutral-300 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-neutral-600">{r.issued_to || "—"}</td>
                    <td className="px-4 py-3 text-right">
                      <Link to={`/outward/${r.id}`} className="text-primary text-xs uppercase tracking-wider hover:underline" data-testid={`outward-open-${r.id}`}>Open →</Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <Pagination
          page={page}
          pageSize={pageSize}
          total={total}
          totalPages={totalPages}
          onPageChange={(n) => load(n)}
          testIdPrefix="outward"
        />
      </div>
    </div>
  );
}
